import {
  actionsKeys,
  formatAmount,
  permissionActions,
} from "@/lib/utils";
import {
  setCurrentFinanceYear,
  setInvoiceDueDate,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetBillingStat } from "@/services";
import { FileChartColumn } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useCollection = () => {
  const [openTabs, setOpenTabs] = useState<string[]>(["collection"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accessPermission, communityData, currentFinanceYear } = useSelector(
    (state: RootState) => state.currentData
  );

  const [collectionCards, setCollectionCards] = useState([
    {
      id: "summary",
      data: [
        {
          label: "# Documents",
          value: 0,
          apiKey: "documentCount",
        },
        {
          label: "Total Collected Amount",
          value: 0,
          apiKey: "collectedAmount",
        },
      ],
      baseApiKey: "cashApp",
      dataHeader: [],
      title: "Cash App",
      link: `${routes.cashApp}`,
      module: actionsKeys?.CASH_APP?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "titleOnly",
      title: "Unified Journal Entry List",
      link: `${routes.journalEntry}`,
      module: actionsKeys?.UNIFIED_JE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Collection 360 Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.ar360}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.COLLECTION_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "owner-trial-balance",
      title: "View",
      desc: "Owner Trial Balance",
      icon: FileChartColumn,
      link: `${routes.ownerTrialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.OWNER_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "report-by-owner",
      title: "View",
      desc: "Report By Owner",
      icon: FileChartColumn,
      link: `${routes.reportByOwner}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.OWNER_BALANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Collection"}</span>,
      },
    ],
    []
  );

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["collection"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    dispatch(setInvoiceDueDate(null));
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const { data: billingStat, isLoading: isBillingStatLoading } =
    useGetBillingStat({
      communityId: communityData?.communityId,
      billingYear: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    if (!billingStat) return;

    // for collection
    const collection = collectionCards.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => {
          if (item.apiKey?.toLowerCase()?.includes("total")) {
            return ({
              ...item,
              value: formatAmount(Number(statData?.[baseApiKey]?.collectedAmountParked + statData?.[baseApiKey]?.collectedAmountPosted))
            })
          }
          return ({
            ...item,
            value: item.apiKey?.toLowerCase()?.includes("amount")
              ? formatAmount(statData?.[baseApiKey]?.[item.apiKey])
              : statData?.[baseApiKey]?.[item.apiKey],
          })
        });

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      if (id === "valueObject") {
        const updatedData = cardData.map((item: any) => {
          const { apiKey: keys, baseApiKey: baseKeys } = item;

          const values = keys.map((key: string, index: number) => {
            const baseKey = baseKeys?.[index];

            const rawValue = baseKey
              ? statData?.[baseApiKey]?.[baseKey]?.[key]
              : statData?.[baseApiKey]?.[key];

            return baseKey?.toLowerCase()?.includes("amount") ||
              key?.toLowerCase()?.includes("amount")
              ? formatAmount(rawValue ?? 0)
              : rawValue ?? 0;
          });

          return {
            ...item,
            value: values,
            access: !!accessPermission?.[card.module]?.[card.accessKeys],
          };
        });

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setCollectionCards(collection);
  }, [billingStat, accessPermission, communityData?.communityId]);

  return {
    communityData,
    collectionCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    isBillingStatLoading,
    onYearChange,
    currentFinanceYear,
  };
};

export default useCollection;
